import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { createFilterOptions } from '@material-ui/lab/useAutocomplete';
import { useIsInBottomSheet } from '../../providers';
import { useBiAction } from '../BiTracking';
import { BottomSheet, BottomSheetContentLayout } from '../BottomSheet';
import { Checkbox } from '../Checkbox/Checkbox';
import { Box, Column, Row } from '../Layout';
import { Searchbox } from '../Searchbox';
import { Typography } from '../Typography';
import styles from './MultiAutocomplete.module.scss';
import { GetMultiAutocompleteTID } from './MultiAutocomplete.utils';
import { MultiAutocompleteChip } from './MultiAutocompleteChip';
function MultiAutocompleteMobileSheet(props) {
    const { options, value, onApply, getOptionId, getOptionLabel, getOptionDisabled, renderOption, onDraftChange, isLoading, applyLabel, noOptionsText, testId, bi, onDone } = props;
    const { MultiAutocompleteTestIds } = useMemo(()=>GetMultiAutocompleteTID(testId), [
        testId
    ]);
    const rootTestId = MultiAutocompleteTestIds.Root();
    const resolvedTestId = testId !== null && testId !== void 0 ? testId : '';
    const logBiAction = useBiAction();
    const [draft, setDraft] = useState(value);
    const [search, setSearch] = useState('');
    const filterOptions = useMemo(()=>createFilterOptions(), []);
    const filteredOptions = search ? filterOptions(options, {
        inputValue: search,
        getOptionLabel
    }) : options;
    const isInDraft = (option)=>draft.some((o)=>getOptionId(o) === getOptionId(option));
    const toggle = (option)=>{
        const wasSelected = isInDraft(option);
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: wasSelected ? 'off' : 'on',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    option_id: getOptionId(option),
                    test_id: resolvedTestId
                })
            });
        }
        const next = wasSelected ? draft.filter((o)=>getOptionId(o) !== getOptionId(option)) : [
            ...draft,
            option
        ];
        setDraft(onDraftChange ? onDraftChange(next, option) : next);
    };
    const apply = ()=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: resolvedTestId
                })
            });
        }
        onApply(draft);
        onDone();
    };
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        footer: {
            primaryAction: {
                text: applyLabel !== null && applyLabel !== void 0 ? applyLabel : 'Apply',
                testId: MultiAutocompleteTestIds.Apply(),
                onClick: apply,
                disabled: isLoading,
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: apply() logs the select BI; instrumenting this Apply button would double-fire.
                noBi: true
            }
        },
        children: [
            /*#__PURE__*/ _jsx(Searchbox, {
                bi: {
                    name: (bi === null || bi === void 0 ? void 0 : bi.name) ? "".concat(bi.name, "_search") : 'search'
                },
                variant: "textfield",
                fluidWidth: true,
                value: search,
                testId: "".concat(rootTestId, "-mobile-searchbox"),
                onChange: (event)=>setSearch(event.target.value),
                onClear: ()=>setSearch('')
            }),
            filteredOptions.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                className: styles.mobileNoOptions,
                testId: "".concat(rootTestId, "-mobile-no-options"),
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : 'No options'
                })
            }) : /*#__PURE__*/ _jsx(Column, {
                children: filteredOptions.map((option, index)=>{
                    var _ref;
                    const selected = isInDraft(option);
                    const disabled = (_ref = getOptionDisabled === null || getOptionDisabled === void 0 ? void 0 : getOptionDisabled(option, draft)) !== null && _ref !== void 0 ? _ref : false;
                    if (renderOption) {
                        return /*#__PURE__*/ _jsx(Box, {
                            className: styles.mobileOption,
                            testId: "".concat(rootTestId, "-mobile-option-").concat(index),
                            onClick: disabled ? undefined : ()=>toggle(option),
                            children: renderOption(option, {
                                selected
                            })
                        }, getOptionId(option));
                    }
                    return /*#__PURE__*/ _jsx(Box, {
                        className: styles.mobileOption,
                        testId: "".concat(rootTestId, "-mobile-option-").concat(index),
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: toggle() logs the option on/off BI; instrumenting this Checkbox would double-fire.
                            noBi: true,
                            checked: selected,
                            disabled: disabled,
                            onChange: disabled ? undefined : ()=>toggle(option),
                            label: getOptionLabel(option),
                            testId: MultiAutocompleteTestIds.Option(getOptionId(option))
                        })
                    }, getOptionId(option));
                })
            })
        ]
    });
}
export function MultiAutocompleteMobile(props) {
    const { value, onApply, getOptionId, getOptionLabel, label, placeholder, testId, bi } = props;
    const { MultiAutocompleteTestIds } = useMemo(()=>GetMultiAutocompleteTID(testId), [
        testId
    ]);
    const rootTestId = MultiAutocompleteTestIds.Root();
    const resolvedTestId = testId !== null && testId !== void 0 ? testId : '';
    const logBiAction = useBiAction();
    const isInBottomSheet = useIsInBottomSheet();
    // eslint-disable-next-line no-restricted-syntax -- WHY: BottomSheet IS the declarative overlay the rule points at, and it takes `open` as a prop, so this seagull surface owns that state itself instead of deferring to showModal.
    const [isOpen, setIsOpen] = useState(false);
    const closeSheet = ()=>setIsOpen(false);
    const removeChip = (id)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: 'off',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    option_id: id,
                    test_id: resolvedTestId
                })
            });
        }
        onApply(value.filter((o)=>getOptionId(o) !== id));
    };
    const openSheet = ()=>setIsOpen(true);
    return /*#__PURE__*/ _jsxs(Column, {
        className: styles.mobileField,
        children: [
            label != null && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "secondary",
                children: label
            }),
            /*#__PURE__*/ _jsx(Box, {
                className: styles.mobileTrigger,
                testId: "".concat(rootTestId, "-trigger"),
                onClick: openSheet,
                role: "button",
                children: value.length === 0 ? /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: placeholder
                }) : /*#__PURE__*/ _jsx(Row, {
                    gap: 4,
                    className: styles.mobileChips,
                    children: value.map((option)=>{
                        const id = getOptionId(option);
                        return /*#__PURE__*/ _jsx(MultiAutocompleteChip, {
                            label: getOptionLabel(option),
                            onRemove: ()=>removeChip(id),
                            testId: MultiAutocompleteTestIds.Chip(id)
                        }, id);
                    })
                })
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: isOpen,
                onClose: closeSheet,
                fullHeight: true,
                closeSheetOnLeave: !isInBottomSheet,
                children: /*#__PURE__*/ _jsx(MultiAutocompleteMobileSheet, _object_spread_props(_object_spread({}, props), {
                    onDone: closeSheet
                }))
            })
        ]
    });
}
